import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import Modal from 'src/modules/shared/components/molecules/modals/modal';
import { Text } from 'src/modules/shared/components/atoms/text';
import { useAlert } from 'src/contexts/alert-context';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import { IntegrationPermission } from 'src/modules/shared/types/permissions.type';
import {
  useConnectIntegration,
  useDisconnectIntegration,
  useEnqueueIntegrationSync,
  useIntegrationActivityEvents,
  useIntegrationConnectedAccount,
  useIntegrations,
  useStartMeliOAuth,
} from 'src/modules/shared/hooks/api/integrations/integrations';
import {
  IntegrationConnectionHealthStatus,
  IntegrationConnectionStatus,
  IntegrationProviderId,
  TCPropertyImportPropertyScope,
  TCPropertyImportSyncMissingPolicy,
} from 'src/api/generated';
import type { IntegrationConnectionUiStatus } from 'src/modules/shared/types/integration-connection-status.type';
import { IntegrationConnectionBadges } from 'src/modules/settings/components/molecules/integrations/integration-connection-badges';
import type {
  IntegrationActionMenuItem,
  MeliIntegrationSelectOption,
} from 'src/modules/settings/types/meli-integration-card.types';
import {
  getModeChangeDescriptionKey,
  hasUnsavedChanges,
  runMeliSync,
  saveMeliConfig,
} from 'src/modules/settings/utils/meli-integration-card.util';
import { extractApiErrorMessage } from 'src/modules/shared/utils/api/error-handler.util';
import { PiArrowClockwise, PiLinkBreak } from 'react-icons/pi';
import { IntegrationCard } from 'src/modules/settings/components/molecules/integrations/integration-card';
import {
  isAutoAssignmentEnabledFromConfig,
  getPropertyImportOptionsFromConfig,
  resolveImportPropertyScope,
  resolveImportSyncMissingPolicy,
} from 'src/modules/settings/utils/integration-import-options.util';
import {
  DEFAULT_FRONTEND_MELI_AUTO_ASSIGNMENT_ENABLED,
  DEFAULT_FRONTEND_MELI_PROPERTY_SCOPE,
  DEFAULT_FRONTEND_MELI_INTEGRATION_MODE,
  DEFAULT_FRONTEND_MELI_SYNC_MISSING_POLICY,
  getMeliIntegrationMode,
  getMeliSellerContactWhatsAppSelection,
  getMeliSellerContactWhatsAppSelectionFromPhone,
  resolveMeliSellerContactWhatsAppSelection,
  serializeMeliSellerContactWhatsAppSelection,
  type FrontendMeliIntegrationMode,
  type FrontendMeliSellerContactWhatsAppSelection,
} from 'src/modules/shared/utils/integrations/meli-integration.util';
import { IntegrationBrandAvatar } from '../../molecules/integrations/integration-brand-avatar';
import { MeliIntegrationCardRightAction } from './meli-integration-card-actions';
import { MeliIntegrationBody } from './meli-integration-card-body';
import { useWhatsAppPhones } from 'src/modules/shared/hooks/api/messaging/whatsapp/phones';
import { MeliIntegrationRecoveryAlert } from '../../molecules/integrations/meli-integration-recovery-alert';

const PROVIDER_ID = IntegrationProviderId.MELI;

export const MeliIntegrationCard: React.FC = () => {
  const { t, language } = useI18n();
  const { showAlert } = useAlert();

  const { hasPermission: canRead } = useHasPermissions(
    IntegrationPermission.Read
  );
  const { hasPermission: canUpdate } = useHasPermissions(
    IntegrationPermission.Update
  );
  const { hasPermission: canDelete } = useHasPermissions(
    IntegrationPermission.Delete
  );
  const { hasPermission: canSyncNow } = useHasPermissions(
    IntegrationPermission.SyncNow
  );

  const {
    integrations,
    loading: loadingIntegrations,
    refetch,
  } = useIntegrations(canRead);

  const meliConnection = useMemo(() => {
    const item =
      integrations?.items.find((i) => i.providerId === PROVIDER_ID) ?? null;
    return item?.connection ?? null;
  }, [integrations]);

  const uiStatus: IntegrationConnectionUiStatus =
    meliConnection?.status ?? 'disconnected';
  const isConnected = uiStatus === IntegrationConnectionStatus.ACTIVE;
  const healthStatus =
    meliConnection?.healthStatus ?? IntegrationConnectionHealthStatus.HEALTHY;

  const { startMeliOAuth, loading: startingOAuth } = useStartMeliOAuth();
  const { connectIntegration, loading: savingConfig } = useConnectIntegration();
  const { disconnectIntegration, loading: disconnecting } =
    useDisconnectIntegration();
  const { enqueueIntegrationSync, loading: syncing } =
    useEnqueueIntegrationSync();
  const {
    connectedAccount,
    loading: loadingConnectedAccount,
    error: connectedAccountError,
  } = useIntegrationConnectedAccount(PROVIDER_ID, canRead && isConnected);
  const {
    phones: whatsappPhones,
    loading: loadingWhatsAppPhones,
    error: whatsappPhonesError,
  } = useWhatsAppPhones({ enabled: canRead && isConnected });

  const [disconnectModalOpen, setDisconnectModalOpen] = useState(false);
  const [confirmModeChangeOpen, setConfirmModeChangeOpen] = useState(false);
  const [mode, setMode] = useState<FrontendMeliIntegrationMode>(
    DEFAULT_FRONTEND_MELI_INTEGRATION_MODE
  );
  const [autoAssignEnabled, setAutoAssignEnabled] = useState<boolean>(
    DEFAULT_FRONTEND_MELI_AUTO_ASSIGNMENT_ENABLED
  );
  const [syncMissingPolicy, setSyncMissingPolicy] =
    useState<TCPropertyImportSyncMissingPolicy>(
      DEFAULT_FRONTEND_MELI_SYNC_MISSING_POLICY
    );
  const [propertyScope, setPropertyScope] =
    useState<TCPropertyImportPropertyScope>(
      DEFAULT_FRONTEND_MELI_PROPERTY_SCOPE
    );
  const [sellerContactWhatsApp, setSellerContactWhatsApp] =
    useState<FrontendMeliSellerContactWhatsAppSelection | null>(null);
  const isInitialLoading = canRead && (loadingIntegrations || !integrations);
  const currentMode = useMemo(
    () => getMeliIntegrationMode(meliConnection?.config ?? null),
    [meliConnection?.config]
  );
  const currentConfig = meliConnection?.config ?? null;
  const currentImportOptions = useMemo(
    () => getPropertyImportOptionsFromConfig(currentConfig),
    [currentConfig]
  );
  const currentAutoAssignEnabled = useMemo(
    () =>
      currentImportOptions == null
        ? DEFAULT_FRONTEND_MELI_AUTO_ASSIGNMENT_ENABLED
        : isAutoAssignmentEnabledFromConfig(currentConfig),
    [currentConfig, currentImportOptions]
  );
  const currentSyncMissingPolicy = useMemo(
    () =>
      currentImportOptions == null
        ? DEFAULT_FRONTEND_MELI_SYNC_MISSING_POLICY
        : resolveImportSyncMissingPolicy(currentConfig),
    [currentConfig, currentImportOptions]
  );
  const currentPropertyScope = useMemo(
    () =>
      currentImportOptions == null
        ? DEFAULT_FRONTEND_MELI_PROPERTY_SCOPE
        : resolveImportPropertyScope(currentConfig),
    [currentConfig, currentImportOptions]
  );
  const persistedSellerContactWhatsApp = useMemo(
    () => getMeliSellerContactWhatsAppSelection(currentConfig),
    [currentConfig]
  );
  const currentSellerContactWhatsApp = useMemo(
    () =>
      resolveMeliSellerContactWhatsAppSelection(
        persistedSellerContactWhatsApp,
        whatsappPhones
      ),
    [persistedSellerContactWhatsApp, whatsappPhones]
  );
  const isSellerContactWhatsAppStale = useMemo(
    () =>
      sellerContactWhatsApp != null &&
      !whatsappPhones.some(
        (phone) =>
          serializeMeliSellerContactWhatsAppSelection(
            getMeliSellerContactWhatsAppSelectionFromPhone(phone)
          ) ===
          serializeMeliSellerContactWhatsAppSelection(sellerContactWhatsApp)
      ),
    [sellerContactWhatsApp, whatsappPhones]
  );
  const { events: integrationActivityEvents } = useIntegrationActivityEvents(
    {
      integrationConnectionId: meliConnection?.id,
      provider: PROVIDER_ID,
      pageSize: 8,
    },
    canRead && meliConnection != null
  );
  const isDirty = hasUnsavedChanges({
    isConnected,
    mode,
    currentMode,
    autoAssignEnabled,
    currentAutoAssignEnabled,
    syncMissingPolicy,
    currentSyncMissingPolicy,
    propertyScope,
    currentPropertyScope,
    sellerContactWhatsApp,
    currentSellerContactWhatsApp,
  });
  const modeChangeDescriptionKey = getModeChangeDescriptionKey(mode);
  const isHealthError =
    healthStatus === IntegrationConnectionHealthStatus.ERROR;
  const isHealthSyncing =
    healthStatus === IntegrationConnectionHealthStatus.SYNCING;
  const canSyncNowConnection =
    canSyncNow && isConnected && currentMode === 'SYNC' && !isHealthError;
  const isBusy = disconnecting || loadingIntegrations;
  const disableSaveButton = !canUpdate || isBusy || !isDirty;

  useEffect(() => {
    setMode(currentMode);
    setAutoAssignEnabled(
      currentImportOptions == null
        ? DEFAULT_FRONTEND_MELI_AUTO_ASSIGNMENT_ENABLED
        : isAutoAssignmentEnabledFromConfig(currentConfig)
    );
    setSyncMissingPolicy(
      currentImportOptions == null
        ? DEFAULT_FRONTEND_MELI_SYNC_MISSING_POLICY
        : resolveImportSyncMissingPolicy(currentConfig)
    );
    setPropertyScope(
      currentImportOptions == null
        ? DEFAULT_FRONTEND_MELI_PROPERTY_SCOPE
        : resolveImportPropertyScope(currentConfig)
    );
    setSellerContactWhatsApp(currentSellerContactWhatsApp);
  }, [
    currentConfig,
    currentImportOptions,
    currentMode,
    currentSellerContactWhatsApp,
  ]);

  const handleStartOAuth = useCallback(async () => {
    try {
      const res = await startMeliOAuth();
      const url = res.authorizationUrl;

      window.location.assign(url);
    } catch (error) {
      const message = extractApiErrorMessage(error);
      showAlert(
        t('settings.integrations.meli.connect.error', { message }),
        'error'
      );
    }
  }, [showAlert, startMeliOAuth, t]);

  const handleDisconnect = useCallback(async () => {
    try {
      await disconnectIntegration({ providerId: PROVIDER_ID });
      setDisconnectModalOpen(false);
      showAlert(t('settings.integrations.meli.disconnect.success'), 'success');
      await refetch();
    } catch (error) {
      const message = extractApiErrorMessage(error);
      showAlert(
        t('settings.integrations.meli.disconnect.error', { message }),
        'error'
      );
    }
  }, [disconnectIntegration, refetch, showAlert, t]);

  const handleSync = useCallback(async () => {
    await runMeliSync({
      enqueueIntegrationSync,
      refetch,
      showAlert,
      t,
    });
  }, [enqueueIntegrationSync, refetch, showAlert, t]);

  const handleSaveClick = useCallback(() => {
    setConfirmModeChangeOpen(isDirty);
  }, [isDirty]);

  const handleSaveConfig = useCallback(async () => {
    await saveMeliConfig({
      meliConnection,
      autoAssignEnabled,
      syncMissingPolicy,
      propertyScope,
      mode,
      sellerContactWhatsApp,
      connectIntegration,
      refetch,
      showAlert,
      t,
      setConfirmModeChangeOpen,
    });
  }, [
    autoAssignEnabled,
    connectIntegration,
    meliConnection,
    mode,
    propertyScope,
    refetch,
    showAlert,
    syncMissingPolicy,
    sellerContactWhatsApp,
    t,
  ]);

  const modeOptions: MeliIntegrationSelectOption[] = [
    {
      value: 'PUBLISH',
      content: t('settings.integrations.meli.mode.options.publish'),
    },
    {
      value: 'SYNC',
      content: t('settings.integrations.meli.mode.options.sync'),
    },
  ];
  const missingPolicyOptions = useMemo(
    () => [
      {
        value: TCPropertyImportSyncMissingPolicy.UPSERT,
        content: t(
          'settings.integrations.meli.syncMissingPolicy.option.upsert'
        ),
      },
      {
        value: TCPropertyImportSyncMissingPolicy.REPLACE,
        content: t(
          'settings.integrations.meli.syncMissingPolicy.option.replace'
        ),
      },
    ],
    [t]
  );
  const propertyScopeOptions = useMemo(
    () => [
      {
        value: TCPropertyImportPropertyScope.ALL,
        content: t('settings.integrations.meli.propertyScope.option.all'),
      },
      {
        value: TCPropertyImportPropertyScope.ACTIVE,
        content: t('settings.integrations.meli.propertyScope.option.active'),
      },
    ],
    [t]
  );
  const sellerContactWhatsAppOptions = useMemo(() => {
    const options: MeliIntegrationSelectOption[] = [
      {
        value: '',
        content: t('settings.integrations.meli.sellerContactWhatsApp.select'),
      },
    ];

    options.push(
      ...whatsappPhones.map((phone) => ({
        value: serializeMeliSellerContactWhatsAppSelection(
          getMeliSellerContactWhatsAppSelectionFromPhone(phone)
        ),
        content: phone.displayName
          ? `${phone.displayName} · ${phone.address}`
          : phone.address,
      }))
    );

    return options;
  }, [t, whatsappPhones]);
  const sellerContactWhatsAppValue =
    sellerContactWhatsApp && !isSellerContactWhatsAppStale
      ? serializeMeliSellerContactWhatsAppSelection(sellerContactWhatsApp)
      : '';
  const actionMenuItems = useMemo<IntegrationActionMenuItem[]>(() => {
    const items: IntegrationActionMenuItem[] = [];

    if (canSyncNowConnection) {
      items.push({
        content: <span>{t('settings.integrations.meli.actions.syncNow')}</span>,
        icon: <PiArrowClockwise className="w-4 h-4" />,
        disabled: syncing || isHealthSyncing,
        onClick: () => void handleSync(),
      });
    }

    if (canDelete) {
      items.push({
        content: (
          <span>{t('settings.integrations.meli.actions.disconnect')}</span>
        ),
        icon: <PiLinkBreak className="w-4 h-4" />,
        disabled: disconnecting,
        onClick: () => setDisconnectModalOpen(true),
        separator: canSyncNowConnection,
      });
    }

    return items;
  }, [
    canDelete,
    canSyncNowConnection,
    disconnecting,
    handleSync,
    isHealthSyncing,
    syncing,
    t,
  ]);

  return (
    <>
      <IntegrationCard
        title={t('settings.integrations.meli.title')}
        subtitle={t('settings.integrations.meli.subtitle')}
        cardProps={{ shadow: 'md', variant: 'primary', appearance: 'outline' }}
        icon={<IntegrationBrandAvatar integrationId="meli" />}
        badges={
          <IntegrationConnectionBadges
            status={uiStatus}
            healthStatus={meliConnection ? healthStatus : null}
            t={t}
          />
        }
        rightAction={
          <MeliIntegrationCardRightAction
            canRead={canRead}
            isInitialLoading={isInitialLoading}
            isConnected={isConnected}
            isBusy={isBusy}
            disableSaveButton={disableSaveButton}
            savingConfig={savingConfig}
            syncing={syncing}
            disconnecting={disconnecting}
            canUpdate={canUpdate}
            startingOAuth={startingOAuth}
            loadingIntegrations={loadingIntegrations}
            actionMenuItems={actionMenuItems}
            t={t}
            onSaveClick={handleSaveClick}
            onStartOAuth={handleStartOAuth}
          />
        }
      >
        <MeliIntegrationRecoveryAlert
          connection={meliConnection}
          canRead={canRead}
          canUpdate={canUpdate}
          canSyncNow={canSyncNowConnection}
          isBusy={isBusy}
          startingOAuth={startingOAuth}
          syncing={syncing}
          onStartOAuth={() => void handleStartOAuth()}
          onSync={() => void handleSync()}
          t={t}
        />
        <MeliIntegrationBody
          canRead={canRead}
          isConnected={isConnected}
          canUpdate={canUpdate}
          disconnecting={disconnecting}
          loadingIntegrations={loadingIntegrations}
          autoAssignEnabled={autoAssignEnabled}
          syncMissingPolicy={syncMissingPolicy}
          propertyScope={propertyScope}
          mode={mode}
          modeOptions={modeOptions}
          missingPolicyOptions={missingPolicyOptions}
          propertyScopeOptions={propertyScopeOptions}
          sellerContactWhatsAppOptions={sellerContactWhatsAppOptions}
          sellerContactWhatsAppValue={sellerContactWhatsAppValue}
          loadingWhatsAppPhones={loadingWhatsAppPhones}
          hasWhatsAppPhonesError={whatsappPhonesError != null}
          hasNoWhatsAppPhones={
            !loadingWhatsAppPhones &&
            whatsappPhonesError == null &&
            whatsappPhones.length === 0
          }
          needsWhatsAppPhoneSelection={
            whatsappPhones.length > 1 && sellerContactWhatsApp == null
          }
          isSellerContactWhatsAppStale={isSellerContactWhatsAppStale}
          connectedAccount={connectedAccount}
          loadingConnectedAccount={loadingConnectedAccount}
          hasConnectedAccountError={connectedAccountError != null}
          integrationActivityEvents={integrationActivityEvents}
          language={language}
          t={t}
          onModeChange={setMode}
          onAutoAssignChange={setAutoAssignEnabled}
          onSyncMissingPolicyChange={setSyncMissingPolicy}
          onPropertyScopeChange={setPropertyScope}
          onSellerContactWhatsAppChange={(value) => {
            const phone = whatsappPhones.find(
              (candidate) =>
                serializeMeliSellerContactWhatsAppSelection(
                  getMeliSellerContactWhatsAppSelectionFromPhone(candidate)
                ) === value
            );
            setSellerContactWhatsApp(
              phone
                ? getMeliSellerContactWhatsAppSelectionFromPhone(phone)
                : null
            );
          }}
        />
      </IntegrationCard>

      <Modal
        isOpen={confirmModeChangeOpen}
        onClose={() => {
          if (!savingConfig) {
            setConfirmModeChangeOpen(false);
          }
        }}
        header={
          <Text size="h6" weight="semibold">
            {t('settings.integrations.meli.mode.confirm.title')}
          </Text>
        }
        body={
          <div className="w-full flex flex-col gap-3">
            <Text size="paragraph">{t(modeChangeDescriptionKey)}</Text>
            <Text size="paragraph">
              {t('settings.integrations.meli.mode.confirm.warning')}
            </Text>
          </div>
        }
        footer={
          <div className="flex justify-end gap-3 px-4 py-3">
            <Button
              variant="neutral"
              appearance="ghost"
              onClick={() => setConfirmModeChangeOpen(false)}
              disabled={savingConfig}
            >
              {t('settings.integrations.meli.mode.confirm.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={() => void handleSaveConfig()}
              loading={savingConfig}
              disabled={savingConfig}
            >
              {t('settings.integrations.meli.mode.confirm.confirm')}
            </Button>
          </div>
        }
        dialogClassName="max-w-lg"
      />

      <Modal
        isOpen={disconnectModalOpen}
        onClose={() => {
          if (!disconnecting) setDisconnectModalOpen(false);
        }}
        header={
          <Text size="h6" weight="semibold">
            {t('settings.integrations.meli.disconnect.title')}
          </Text>
        }
        body={
          <div className="w-full space-y-2">
            <Text size="paragraph">
              {t('settings.integrations.meli.disconnect.description')}
            </Text>
          </div>
        }
        footer={
          <div className="flex justify-end gap-3 px-4 py-3">
            <Button
              variant="neutral"
              appearance="ghost"
              onClick={() => setDisconnectModalOpen(false)}
              disabled={disconnecting}
            >
              {t('settings.integrations.meli.disconnect.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={() => void handleDisconnect()}
              loading={disconnecting}
              disabled={disconnecting}
            >
              {t('settings.integrations.meli.disconnect.confirm')}
            </Button>
          </div>
        }
        dialogClassName="max-w-lg"
      />
    </>
  );
};
